import { useState, useCallback, useRef } from 'react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@creanizer/ui'
import { Alert, AlertDescription, AlertTitle } from '@creanizer/ui'
import { Button } from '@creanizer/ui'
import { Progress } from '@creanizer/ui'
import { AlertCircle, MapPin } from 'lucide-react'
import { LocationForm } from './LocationForm'
import { LocationResult } from './LocationResult'
import { SavedLocationsList } from './SavedLocationsList'
import { useGenerator } from '../../../../hooks/useGenerator'
import { useStreamGenerator } from '../../../../hooks/useStreamGenerator'
import { useLocationStore } from '../../../../stores/locationStore'
import { useWorldBuilderStore } from '../../../../stores/worldbuilderStore'
import { useCommunityStore } from '../../../../stores/communityStore'
import { useEnemyStore } from '../../../../stores/enemyStore'
import { useItemStore } from '../../../../stores/itemStore'
import { useCharacterStore } from '../../../../stores/characterStore'
import { generateCharacterContext, generateEnemyContext, generateItemContext } from '@creanizer/utils'
import type { LocationInput, LocationResult as LocationResultType } from '@creanizer/types'

export function LocationGenerator() {
  const [activeTab, setActiveTab] = useState('generate')
  const [currentResult, setCurrentResult] = useState<LocationResultType | null>(null)
  const [currentBatchResult, setCurrentBatchResult] = useState<LocationResultType[] | null>(null)
  const [isSaved, setIsSaved] = useState(false)
  const [currentInput, setCurrentInput] = useState<LocationInput | null>(null)
  const [useStreaming, setUseStreaming] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const resultRef = useRef<HTMLDivElement>(null)
  
  const generator = useGenerator('location')
  const streamGenerator = useStreamGenerator('location')
  const { 
    addLocation, 
    addBatchLocations, 
    updateLocation,
    exportLocations,
    importLocations,
    getStatistics 
  } = useLocationStore()
  const { savedWorlds } = useWorldBuilderStore()
  const { savedCommunities } = useCommunityStore()
  const { enemies: savedEnemies } = useEnemyStore()
  const { items: savedItems } = useItemStore()
  const { characters: savedCharacters } = useCharacterStore()

  const handleGenerate = useCallback(async (input: LocationInput) => {
    setError(null)
    setCurrentResult(null)
    setCurrentBatchResult(null)
    setIsSaved(false)
    setCurrentInput(input)
    
    try {
      // 世界観コンテキストの取得（全データ）
      if (input.worldContext) {
        const world = savedWorlds.find(w => w.id === input.worldContext)
        if (world) {
          // 全データをJSONで送信
          input.worldContext = JSON.stringify(world.result)
        }
      }
      
      // コミュニティコンテキストの取得（全データ）
      if (input.communityContext) {
        const community = savedCommunities.find(c => c.id === input.communityContext)
        if (community) {
          // 全データをJSONで送信
          input.communityContext = JSON.stringify(community.result)
        }
      }
      
      // 関連エネミーコンテキストの取得
      if (input.relatedEnemyContext) {
        const enemy = savedEnemies.find(e => e.id === input.relatedEnemyContext)
        if (enemy) {
          input.relatedEnemyContext = generateEnemyContext(enemy)
        }
      }
      
      // 関連アイテムコンテキストの取得
      if (input.relatedItemContext) {
        const item = savedItems.find(i => i.id === input.relatedItemContext)
        if (item) {
          input.relatedItemContext = generateItemContext(item)
        }
      }
      
      // 関連キャラクターコンテキストの取得
      if (input.relatedCharacterContext) {
        const character = savedCharacters.find(c => c.id === input.relatedCharacterContext)
        if (character) {
          input.relatedCharacterContext = generateCharacterContext(character)
        }
      }
      
      if (useStreaming) {
        await streamGenerator.generateStream(input, {
          onChunk: (chunk) => {
            console.log('Received chunk:', chunk.length)
          },
          onProgress: (bytes) => {
            console.log('Progress:', bytes)
          },
          onComplete: (resultData) => {
            if (resultData !== null && resultData !== undefined) {
              // バッチモードかチェック
              if (Array.isArray(resultData)) {
                handleResult(resultData as LocationResultType[])
              } else if (typeof resultData === 'object' && ('basicInfo' in resultData || 'name' in resultData)) {
                handleResult(resultData as LocationResultType)
              } else {
                console.error('Invalid result structure:', resultData)
                if (resultData) {
                  console.warn('Setting result despite structure issues')
                  handleResult(resultData as LocationResultType | LocationResultType[])
                } else {
                  setError('結果のパースに失敗しました')
                }
              }
            } else {
              console.error('Incomplete result received:', resultData)
              setError('結果のパースに失敗しました')
            }
          },
          onError: (error) => {
            console.error('Streaming error:', error)
            setError(error.message)
            
            // ストリーミングエラーの場合は通常モードにフォールバック
            if (error.message.includes('ストリーミング')) {
              setUseStreaming(false)
              generator.mutate(input)
            }
          }
        })
      } else {
        const response = await generator.mutateAsync(input)
        if (response?.result) {
          handleResult(response.result as LocationResultType | LocationResultType[])
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'ロケーション生成中にエラーが発生しました')
    }
  }, [generator, streamGenerator, useStreaming, savedWorlds, savedCommunities, savedEnemies, savedItems, savedCharacters])
  
  const handleResult = useCallback((result: LocationResultType | LocationResultType[]) => {
    if (Array.isArray(result)) {
      // バッチモードの場合
      setCurrentBatchResult(result)
      setCurrentResult(result[0])
    } else {
      // 通常モードの場合
      setCurrentResult(result)
      setCurrentBatchResult(null)
    }
    
    setTimeout(() => {
      resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 100)
  }, [])

  const handleSave = useCallback(() => {
    if (!currentResult || !currentInput) return
    
    if (currentBatchResult) {
      // バッチモードの場合
      addBatchLocations(
        currentBatchResult.map((location) => ({
          title: location.basicInfo.name,
          input: currentInput,
          result: location,
          updatedAt: new Date().toISOString()
        }))
      )
    } else {
      // 通常モードの場合
      addLocation({
        title: currentResult.basicInfo.name,
        input: currentInput,
        result: currentResult,
        updatedAt: new Date().toISOString()
      })
    }
    
    setIsSaved(true)
  }, [currentResult, currentInput, currentBatchResult, addLocation, addBatchLocations])

  const handleLoad = useCallback((location: { input: LocationInput; result: LocationResultType }) => {
    setCurrentInput(location.input)
    setCurrentResult(location.result)
    setIsSaved(true)
    setActiveTab('generate')
  }, [])
  
  const handleExport = useCallback(() => {
    const jsonString = exportLocations()
    const blob = new Blob([jsonString], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `locations_${new Date().toISOString().split('T')[0]}.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }, [exportLocations])
  
  const handleImport = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return
    
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string
        importLocations(content)
        setActiveTab('saved')
      } catch (error) {
        console.error('Import failed:', error)
        alert('インポートに失敗しました。ファイル形式を確認してください。')
      }
    }
    reader.readAsText(file)
  }, [importLocations])

  const stats = getStatistics()

  return (
    <div className="space-y-6">
      <div className="card">
        <div className="flex items-center gap-2 mb-2">
          <MapPin className="h-5 w-5" />
          <h2 className="text-xl font-semibold">ロケーションジェネレータ</h2>
        </div>
        <p className="text-muted-foreground mb-4">
          屋外環境・自然地形・野外冒険の舞台を生成します
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="generate">生成</TabsTrigger>
          <TabsTrigger value="saved">
            保存済み ({stats.total}/500)
          </TabsTrigger>
        </TabsList>

        <TabsContent value="generate" className="space-y-6">
          <div className="card">
            <h3 className="text-lg font-semibold mb-4">生成設定</h3>
            <LocationForm
              onGenerate={handleGenerate}
              isGenerating={generator.isPending || streamGenerator.isStreaming}
              initialData={isSaved && currentInput ? currentInput : undefined}
              useStreaming={useStreaming}
              onStreamingChange={setUseStreaming}
            />
          </div>
          
          {streamGenerator.isStreaming && (
            <div className="card">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>生成中...</span>
                  <span>{Math.round(streamGenerator.progress)}%</span>
                </div>
                <Progress value={streamGenerator.progress} />
                {streamGenerator.partialContent && (
                  <div className="mt-4 p-4 bg-muted rounded max-h-48 overflow-y-auto">
                    <pre className="text-xs whitespace-pre-wrap">
                      {streamGenerator.partialContent.slice(-500)}
                      {streamGenerator.partialContent.length > 500 && '...'}
                    </pre>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>エラー</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </TabsContent>

        <TabsContent value="saved">
          <SavedLocationsList
            onLoad={handleLoad}
            onExport={handleExport}
            onImport={handleImport}
          />
        </TabsContent>
      </Tabs>
      
      {currentResult && (
        <div className="card" ref={resultRef}>
          <LocationResult
            result={currentResult}
            batchResults={currentBatchResult}
            onSave={handleSave}
            isSaved={isSaved}
          />
        </div>
      )}
    </div>
  )
}